import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/Layout';
import { fiscal, orders as ordersApi } from '@/lib/api';
import { toast } from 'sonner';
import {
  FileText, Settings, Shield, TrendingUp, Plus, Search, X,
  CheckCircle, AlertCircle, XCircle, RefreshCw, Eye, Ban, FileDown,
  Archive, Receipt, ChevronDown
} from 'lucide-react';

const TABS = [
  { id: 'documents', label: 'Notas Fiscais' },
  { id: 'emit', label: 'Emitir NFC-e' },
  { id: 'tax', label: 'Perfis Tributarios' },
  { id: 'config', label: 'Configuracoes' },
  { id: 'reports', label: 'Relatorios' },
];

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}
function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function DocStatusBadge({ status }) {
  const styles = {
    RASCUNHO: 'bg-slate-100 text-slate-600',
    PENDENTE: 'bg-amber-100 text-amber-700',
    TRANSMITINDO: 'bg-blue-100 text-blue-700',
    PROCESSANDO: 'bg-blue-100 text-blue-700',
    AUTORIZADA: 'bg-emerald-100 text-emerald-700',
    EMITIDA: 'bg-emerald-100 text-emerald-700',
    REJEITADA: 'bg-red-100 text-red-700',
    ERRO: 'bg-red-100 text-red-700',
    CANCELADA: 'bg-slate-200 text-slate-500',
    INUTILIZADA: 'bg-orange-100 text-orange-700',
    SIMULADA: 'bg-slate-200 text-rose-700 border border-rose-300',
  };
  return (
    <span data-testid={`doc-status-${status}`} className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100'}`}>
      {status}
    </span>
  );
}

function SimuladaTag() {
  return (
    <span
      data-testid="doc-simulada-tag"
      title="Documento nao fiscal (simulado)"
      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200"
    >
      <AlertCircle size={10} /> SIMULADA
    </span>
  );
}

function DocTypeBadge({ type }) {
  const styles = {
    NFCE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    NFE: 'bg-blue-50 text-blue-700 border-blue-200',
    ENTRADA_XML: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${styles[type] || 'bg-slate-50'}`}>
      {type}
    </span>
  );
}

// ============ MODAL: CPF NA NOTA / EDITAR DESTINATARIO ============
function maskCpfDisplay(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
function validateCpfClient(cpf) {
  const d = String(cpf || '').replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  for (let i = 9; i < 11; i++) {
    let s = 0;
    for (let n = 0; n < i; n++) s += parseInt(d[n], 10) * ((i + 1) - n);
    const v = ((s * 10) % 11) % 10;
    if (v !== parseInt(d[i], 10)) return false;
  }
  return true;
}

function FiscalDestinationModal({ orderId, mode = 'capture', onClose, onConfirm }) {
  // mode: 'capture' (pre Gerar XML) | 'edit' (override apos doc criado)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [includeDest, setIncludeDest] = useState(true);
  const [form, setForm] = useState({
    cpf: '', name: '', email: '', phone: '',
    address_cep: '', address_street: '', address_number: '',
    address_complement: '', address_neighborhood: '',
    address_city: '', address_state: '',
  });
  const [showAddress, setShowAddress] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    fiscal.destination.get(orderId)
      .then(r => {
        setInfo(r.data);
        const src = r.data.override || r.data.effective;
        if (src) {
          setIncludeDest(true);
          setForm(f => ({
            ...f,
            cpf: src.cpf || '',
            name: src.name || '',
            email: src.email || '',
            phone: src.phone || '',
            address_cep: src.address_cep || '',
            address_street: src.address_street || '',
            address_number: src.address_number || '',
            address_complement: src.address_complement || '',
            address_neighborhood: src.address_neighborhood || '',
            address_city: src.address_city || '',
            address_state: src.address_state || '',
          }));
          if (src.address_street) setShowAddress(true);
        } else {
          setIncludeDest(false);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  const cpfValid = !form.cpf || validateCpfClient(form.cpf);
  const canSubmit = !includeDest || (form.cpf && cpfValid);

  const handleSave = async (alsoTransmitAfter) => {
    setSaving(true);
    try {
      if (includeDest) {
        const payload = {
          cpf: String(form.cpf || '').replace(/\D/g, ''),
          name: form.name || null,
          email: form.email || null,
          phone: form.phone || null,
        };
        if (showAddress) {
          Object.assign(payload, {
            address_cep: form.address_cep, address_street: form.address_street,
            address_number: form.address_number, address_complement: form.address_complement,
            address_neighborhood: form.address_neighborhood,
            address_city: form.address_city, address_state: form.address_state,
          });
        }
        await fiscal.destination.upsert(orderId, payload);
      } else {
        // Marcar explicitamente como anonimo: remove override existente
        try { await fiscal.destination.remove(orderId); } catch (_) { /* sem override antes */ }
      }
      onConfirm && onConfirm({ alsoTransmitAfter, includeDest });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar destinatario');
    } finally {
      setSaving(false);
    }
  };

  if (!orderId) return null;
  const supports = info?.supports_dest !== false;

  return (
    <div
      data-testid="fiscal-dest-modal"
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="text-lg font-semibold text-slate-800" data-testid="fiscal-dest-modal-title">
              {mode === 'edit' ? 'Editar destinatario fiscal' : 'CPF na nota?'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {mode === 'edit'
                ? 'Alteracoes geram OVERRIDE na NFC-e (auditado). Nao altera o cadastro do cliente.'
                : 'Identificar o cliente na NFC-e (opcional). Nao altera o cadastro do CRM.'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="fiscal-dest-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Carregando…</div>
          ) : !supports ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              Este canal ({info?.origin || 'desconhecido'}) emite NFC-e sempre como
              <strong> consumidor anonimo</strong>. Captura de dados nao disponivel.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="fiscal-dest-toggle">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio" name="dest-mode" checked={includeDest}
                    onChange={() => setIncludeDest(true)}
                    data-testid="fiscal-dest-include"
                  />
                  <span className="text-sm font-medium text-slate-700">Identificar cliente</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio" name="dest-mode" checked={!includeDest}
                    onChange={() => setIncludeDest(false)}
                    data-testid="fiscal-dest-anonymous"
                  />
                  <span className="text-sm text-slate-600">Consumidor anonimo</span>
                </label>
              </div>

              {includeDest && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium">CPF *</label>
                      <input
                        autoFocus
                        value={maskCpfDisplay(form.cpf)}
                        onChange={(e) => setForm(f => ({ ...f, cpf: e.target.value.replace(/\D/g, '') }))}
                        placeholder="000.000.000-00"
                        inputMode="numeric"
                        maxLength={14}
                        className={`w-full px-3 py-2.5 rounded-lg border text-base font-mono ${form.cpf && !cpfValid ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                        data-testid="fiscal-dest-cpf"
                      />
                      {form.cpf && !cpfValid && (
                        <p className="text-xs text-red-600 mt-1" data-testid="fiscal-dest-cpf-error">CPF invalido</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium">Nome (opcional)</label>
                      <input
                        value={form.name}
                        onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Nome do cliente"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-base"
                        data-testid="fiscal-dest-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium">Email (opcional)</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="cliente@exemplo.com"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-base"
                        data-testid="fiscal-dest-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 font-medium">Telefone (opcional)</label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))}
                        placeholder="11999999999"
                        inputMode="numeric"
                        maxLength={11}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-base"
                        data-testid="fiscal-dest-phone"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAddress(s => !s)}
                    className="text-xs text-slate-600 hover:text-slate-800 underline"
                    data-testid="fiscal-dest-toggle-address"
                  >
                    {showAddress ? '− Ocultar endereco fiscal' : '+ Adicionar endereco fiscal (opcional)'}
                  </button>

                  {showAddress && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="fiscal-dest-address">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-slate-500">CEP</label>
                          <input
                            value={form.address_cep}
                            onChange={(e) => setForm(f => ({ ...f, address_cep: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                            data-testid="fiscal-dest-cep"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-slate-500">Logradouro</label>
                          <input value={form.address_street}
                            onChange={(e) => setForm(f => ({ ...f, address_street: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                            data-testid="fiscal-dest-street" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-slate-500">Numero</label>
                          <input value={form.address_number}
                            onChange={(e) => setForm(f => ({ ...f, address_number: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                            data-testid="fiscal-dest-number" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-slate-500">Complemento</label>
                          <input value={form.address_complement}
                            onChange={(e) => setForm(f => ({ ...f, address_complement: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-slate-500">Bairro</label>
                          <input value={form.address_neighborhood}
                            onChange={(e) => setForm(f => ({ ...f, address_neighborhood: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Cidade</label>
                          <input value={form.address_city}
                            onChange={(e) => setForm(f => ({ ...f, address_city: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">UF</label>
                          <input value={form.address_state}
                            maxLength={2}
                            onChange={(e) => setForm(f => ({ ...f, address_state: e.target.value.toUpperCase() }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-xs text-slate-500">
            {info?.source && info.source !== 'ANONIMO' && (
              <span data-testid="fiscal-dest-source-badge">
                Origem: <strong>{info.source}</strong>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-100"
              data-testid="fiscal-dest-cancel">
              Cancelar
            </button>
            <button onClick={() => handleSave(false)} disabled={!canSubmit || saving}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="fiscal-dest-confirm">
              {saving ? 'Salvando…' : (mode === 'edit' ? 'Salvar override' : 'Confirmar e gerar XML')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============ FISCAL SANITY HEADER ============
function FiscalSanityHeader({ stats, onArchive, onRefresh }) {
  if (!stats) return null;
  const hasSimuladas = (stats.total_simuladas || 0) > 0;

  const handleArchive = async () => {
    if (!window.confirm('Arquivar todas as NFC-e simuladas? Elas nao serao excluidas, apenas ocultadas.')) return;
    try {
      const r = await fiscal.archiveSimulated();
      toast.success(`${r.data.archived_count} nota(s) simulada(s) arquivada(s)`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao arquivar');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="fiscal-sanity-header">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="stat-total-nfce">
            <span className="text-xs text-slate-500 mr-2">NFC-e</span>
            <span className="text-base font-bold text-slate-800">{stats.total_nfce}</span>
          </div>
          <div
            title="Notas simuladas nao possuem validade fiscal"
            data-testid="stat-total-simuladas"
            className={`px-3 py-2 rounded-lg border ${hasSimuladas ? 'bg-rose-50 border-rose-200 ring-1 ring-rose-300' : 'bg-slate-50 border-slate-200'}`}
          >
            <span className={`text-xs mr-2 ${hasSimuladas ? 'text-rose-600' : 'text-slate-500'}`}>Simuladas</span>
            <span className={`text-base font-bold ${hasSimuladas ? 'text-rose-700' : 'text-slate-700'}`}>{stats.total_simuladas}</span>
          </div>
          <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="stat-total-autorizadas">
            <span className="text-xs text-emerald-600 mr-2">Autorizadas</span>
            <span className="text-base font-bold text-emerald-700">{stats.total_autorizadas}</span>
          </div>
          {stats.total_simuladas_arquivadas > 0 && (
            <div className="px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-500" data-testid="stat-total-arquivadas">
              Arquivadas: <span className="font-semibold text-slate-700">{stats.total_simuladas_arquivadas}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasSimuladas ? (
            <>
              <span className="text-xs text-rose-600 font-medium" data-testid="fiscal-progress-warning">
                Ainda existem {stats.total_simuladas} NFC-e simuladas
              </span>
              <button
                onClick={handleArchive}
                data-testid="archive-simulated-btn"
                className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium"
              >
                <Archive size={14} /> Arquivar simuladas
              </button>
            </>
          ) : (
            <span
              data-testid="fiscal-progress-clean"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold"
            >
              <CheckCircle size={14} /> Base fiscal limpa
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ DANFE PREVIEW MODAL ============
function DanfePreviewModal({ docId, onClose, onAfterRegenerate }) {
  const navigate = useNavigate();
  const [html, setHtml] = useState('');
  const [meta, setMeta] = useState(null); // { alerts, can_transmit, order_id, ... }
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    if (!docId) return;
    setLoading(true);
    Promise.all([
      fiscal.previewHtml(docId).then(r => (typeof r.data === 'string' ? r.data : '')),
      fiscal.previewJson(docId).then(r => r.data),
    ])
      .then(([h, j]) => { setHtml(h); setMeta(j); })
      .catch(e => setErr(e.response?.data?.detail || 'Erro ao carregar preview'))
      .finally(() => setLoading(false));
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await fiscal.regenerateXml(docId);
      toast.success('XML regenerado');
      load();
      onAfterRegenerate && onAfterRegenerate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao regenerar XML');
    } finally {
      setRegenerating(false);
    }
  };

  const handleGoToOrder = () => {
    if (meta?.order_id) {
      navigate(`/orders?focus=${meta.order_id}#fiscal`);
      onClose();
    }
  };

  if (!docId) return null;

  const alerts = meta?.alerts || [];
  const critical = alerts.filter(a => a.level === 'CRITICAL');
  const warning = alerts.filter(a => a.level === 'WARNING');
  const canTransmit = meta?.can_transmit !== false;
  const docStatus = (meta?.doc_status || '').toUpperCase();
  const isAuthorized = docStatus === 'AUTORIZADA' || docStatus === 'EMITIDA';
  const isCancelled = docStatus === 'CANCELADA';

  const handlePrint = () => {
    const iframe = document.querySelector('[data-testid="danfe-preview-iframe"]');
    if (iframe?.contentWindow) {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) {}
    }
  };

  const handleDownloadXml = async () => {
    try {
      const r = await fiscal.downloadXml(docId, isAuthorized ? 'proc' : 'signed');
      const stage = r.headers?.['x-fiscal-xml-stage'] || '';
      const prefix = stage === 'AUTHORIZED' ? 'nfeProc' : 'NFCe';
      const blob = new Blob([r.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${prefix}_${meta?.access_key || docId}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('XML baixado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao baixar XML'); }
  };

  return (
    <div
      data-testid="danfe-preview-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Receipt size={18} className={isAuthorized ? 'text-emerald-600' : (isCancelled ? 'text-red-600' : 'text-amber-600')} />
            <h3 className="font-semibold text-slate-800" data-testid="danfe-modal-title">
              {isAuthorized ? 'DANFE NFC-e — Autorizada' : (isCancelled ? 'DANFE NFC-e — Cancelada' : 'Pre-visualizacao DANFE NFC-e')}
            </h3>
            {isAuthorized ? (
              <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="danfe-authorized-badge">
                cStat 100 · Protocolo {meta?.sefaz_protocol || meta?.protocolo || '—'}
              </span>
            ) : isCancelled ? (
              <span className="text-[10px] uppercase tracking-wide text-red-700 font-bold bg-red-50 border border-red-200 rounded px-2 py-0.5">
                Cancelada
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide text-rose-600 font-bold">sem valor fiscal</span>
            )}
          </div>
          <button onClick={onClose} data-testid="danfe-preview-close" className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        {/* Validation bar (so para pre-transmissao) */}
        {!loading && !err && meta && !isAuthorized && !isCancelled && (
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50" data-testid="validation-bar">
            <div className="flex flex-wrap items-center gap-2">
              {alerts.length === 0 && (
                <span
                  data-testid="validation-ok"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold"
                  title="Nenhuma inconsistencia detectada — pronto para a etapa de assinatura/transmissao"
                >
                  <CheckCircle size={14} /> Pronto para transmissao
                </span>
              )}
              {critical.map(a => (
                <span
                  key={a.code}
                  data-testid={`alert-critical-${a.code}`}
                  title={a.msg}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-100 border border-rose-300 text-rose-800 text-xs font-bold"
                >
                  <XCircle size={14} /> {a.label || a.code}
                </span>
              ))}
              {warning.map((a, i) => (
                <span
                  key={`${a.code}-${i}`}
                  data-testid={`alert-warning-${a.code}`}
                  title={a.msg}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold"
                >
                  <AlertCircle size={14} /> {a.label || a.code}
                </span>
              ))}

              <div className="flex-1" />

              {!canTransmit && (
                <span data-testid="cannot-transmit-flag" className="text-xs font-bold text-rose-700">
                  Bloqueado para transmissao
                </span>
              )}

              {meta.order_id && (
                <button
                  onClick={handleGoToOrder}
                  data-testid="go-to-order-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-medium"
                  title="Abrir o pedido para corrigir produtos/pagamento"
                >
                  Corrigir no pedido
                </button>
              )}

              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                data-testid="regenerate-xml-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold"
                title="Regerar XML com os dados atuais do pedido"
              >
                <RefreshCw size={12} /> {regenerating ? 'Regerando...' : 'Corrigir e regerar XML'}
              </button>
            </div>
          </div>
        )}

        {/* Barra de acoes pos-autorizacao */}
        {!loading && !err && meta && (isAuthorized || isCancelled) && (
          <div className="px-5 py-3 border-b border-slate-200 bg-emerald-50" data-testid="danfe-actions-bar">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs text-slate-700">
                <strong>Chave:</strong>{' '}
                <span className="font-mono">{meta?.access_key?.replace(/(\d{4})/g, '$1 ').trim()}</span>
              </div>
              <div className="flex-1" />
              <button onClick={handlePrint}
                data-testid="danfe-print-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold">
                <FileText size={12} /> Imprimir
              </button>
              <button onClick={handleDownloadXml}
                data-testid="danfe-download-xml-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
                <FileDown size={12} /> Baixar XML (nfeProc)
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-slate-100">
          {loading && <div className="p-8 text-center text-slate-500">Carregando preview...</div>}
          {err && <div className="p-8 text-center text-rose-600">{err}</div>}
          {!loading && !err && (
            <iframe
              data-testid="danfe-preview-iframe"
              srcDoc={html}
              title="DANFE Preview"
              sandbox="allow-same-origin"
              className="w-full h-[70vh] border-0 bg-slate-100"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============ DOCUMENTS TAB ============
function FiscalHealthCard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = () => {
    setLoading(true);
    fiscal.health().then(r => setHealth(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);
  if (!health) return null;
  const c = health.certificate || {};
  const s = health.nfce_stats || {};
  const cert_color = c.error ? 'text-red-600' : (c.warning ? 'text-amber-600' : 'text-emerald-600');
  const approval_color = (s.approval_rate ?? 100) >= 90 ? 'text-emerald-600' : (s.approval_rate >= 70 ? 'text-amber-600' : 'text-red-600');
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="fiscal-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-slate-500" />
          <h3 className="font-semibold text-slate-800">Saúde Fiscal</h3>
          {health.strict_mode && (
            <span className="px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-700 rounded">MODO ESTRITO</span>
          )}
        </div>
        <button onClick={refresh} disabled={loading} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="fiscal-health-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
        <div className="p-2.5 rounded-lg bg-slate-50">
          <div className="text-slate-500">Schemas XSD</div>
          <div className={`font-semibold mt-0.5 ${health.schemas_loaded ? 'text-emerald-600' : 'text-red-600'}`}>
            {health.schemas_loaded ? '✓ Carregados' : '⚠ Não carregados'}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">{health.schemas_available?.length || 0} XSDs</div>
        </div>
        <div className="p-2.5 rounded-lg bg-slate-50">
          <div className="text-slate-500">Certificado A1</div>
          <div className={`font-semibold mt-0.5 ${cert_color}`} data-testid="fiscal-health-cert">
            {c.loaded ? (c.days_to_expiry !== null ? `${c.days_to_expiry}d até expirar` : '✓ OK') : '⚠ Não carregado'}
          </div>
          {(c.warning || c.error) && <div className="text-[10px] text-amber-600 mt-0.5">{c.warning || c.error}</div>}
        </div>
        <div className="p-2.5 rounded-lg bg-slate-50">
          <div className="text-slate-500">CSC / Ambiente</div>
          <div className="font-semibold text-slate-800 mt-0.5">
            {health.fiscal_config?.environment || '?'} / id {health.fiscal_config?.csc_id || '?'}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">{health.fiscal_config?.csc_present ? 'CSC OK' : 'CSC ausente'}</div>
        </div>
        <div className="p-2.5 rounded-lg bg-slate-50">
          <div className="text-slate-500">Taxa de autorização</div>
          <div className={`font-semibold mt-0.5 ${approval_color}`} data-testid="fiscal-health-approval">
            {s.approval_rate ?? '—'}%
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">{s.autorizadas}/{s.total_last_50} (últimas 50)</div>
        </div>
        <div className="p-2.5 rounded-lg bg-slate-50">
          <div className="text-slate-500">Última rejeição</div>
          <div className="font-semibold text-slate-800 mt-0.5">
            {health.last_rejection ? `cStat ${health.last_rejection.cstat}` : '—'}
          </div>
          {health.last_rejection?.xmotivo && (
            <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={health.last_rejection.xmotivo}>
              {health.last_rejection.xmotivo.slice(0, 50)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ doc_type: '', status: '', include_archived: false });
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filters.doc_type) params.doc_type = filters.doc_type;
    if (filters.status) params.status = filters.status;
    if (filters.include_archived) params.include_archived = true;
    fiscal.documents.list(params).then(r => setDocs(r.data));
    fiscal.stats().then(r => setStats(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar este documento fiscal?')) return;
    try { await fiscal.documents.cancel(id); toast.success('Documento cancelado'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleReprocess = async (id) => {
    try { await fiscal.documents.reprocess(id); toast.success('Documento reprocessado'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleDownloadXml = async (id, key, format = 'auto') => {
    try {
      const r = await fiscal.downloadXml(id, format);
      const stage = r.headers?.['x-fiscal-xml-stage'] || 'AUTO';
      const prefix = stage === 'AUTHORIZED' ? 'nfeProc' : 'NFCe';
      const blob = new Blob([r.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${prefix}_${key || id}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (stage === 'AUTHORIZED') {
        toast.success('XML oficial baixado (nfeProc — para arquivamento/contador)');
      } else if (stage === 'SIGNED') {
        toast.success('XML assinado baixado (NFe — para validador SEFAZ)');
      } else {
        toast('XML rascunho baixado (sem assinatura)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao baixar XML');
    }
  };

  const [transmittingId, setTransmittingId] = useState(null);
  const [editDestOrderId, setEditDestOrderId] = useState(null);
  const [editDestDocId, setEditDestDocId] = useState(null);
  const handleTransmit = async (id) => {
    if (!window.confirm('Transmitir esta NFC-e para SEFAZ? A operacao pode demorar alguns segundos.')) return;
    setTransmittingId(id);
    try {
      const r = await fiscal.transmitNfce(id);
      if (r.data.status === 'AUTORIZADA') {
        toast.success(`NFC-e AUTORIZADA — protocolo ${r.data.protocolo || r.data.cstat}`);
      } else {
        toast.error(`Rejeitada: ${r.data.motivo || r.data.cstat || 'erro'}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao transmitir');
    } finally {
      setTransmittingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="fiscal-documents-tab">
      <FiscalHealthCard />
      <FiscalSanityHeader stats={stats} onRefresh={load} />
      <div className="flex gap-3 flex-wrap items-center">
        <select value={filters.doc_type} onChange={e => setFilters(f => ({ ...f, doc_type: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="doc-filter-type">
          <option value="">Todos os Tipos</option>
          <option value="NFCE">NFC-e</option><option value="NFE">NF-e</option><option value="ENTRADA_XML">Entrada XML</option>
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="doc-filter-status">
          <option value="">Todos os Status</option>
          <option value="AUTORIZADA">Autorizada</option>
          <option value="EMITIDA">Emitida</option><option value="CANCELADA">Cancelada</option>
          <option value="REJEITADA">Rejeitada</option>
          <option value="ERRO">Erro</option><option value="PROCESSANDO">Processando</option>
          <option value="PENDENTE">Pendente</option>
          <option value="SIMULADA">Simulada (nao fiscal)</option>
        </select>
        <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="doc-refresh">
          <RefreshCw size={16} />
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer ml-2" data-testid="filter-include-archived">
          <input
            type="checkbox"
            checked={filters.include_archived}
            onChange={e => setFilters(f => ({ ...f, include_archived: e.target.checked }))}
            className="rounded border-slate-300"
          />
          Mostrar arquivadas
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Tipo</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Numero</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Destinatario</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Valor</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Data</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => {
              const isSimulated = doc.is_simulated || doc.status === 'SIMULADA';
              return (
              <tr key={doc.id}
                className={`border-b border-slate-100 hover:bg-slate-50 ${isSimulated ? 'bg-rose-50/30' : ''}`}
                data-testid={`doc-row-${doc.id}`}
                data-simulated={isSimulated ? 'true' : 'false'}>
                <td className="px-4 py-3"><DocTypeBadge type={doc.doc_type} /></td>
                <td className="px-4 py-3 font-mono text-slate-700">
                  {doc.number || '-'}
                  {isSimulated && <SimuladaTag />}
                </td>
                <td className="px-4 py-3 text-slate-700">{doc.customer_name || doc.supplier_name || '-'}</td>
                <td className="px-4 py-3 text-right font-medium">{formatCurrency(doc.total_value)}</td>
                <td className="px-4 py-3 text-center"><DocStatusBadge status={isSimulated ? 'SIMULADA' : doc.status} /></td>
                <td className="px-4 py-3 text-slate-500">{formatDate(doc.issued_at || doc.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-center items-center">
                    <button onClick={() => setSelectedDoc(selectedDoc?.id === doc.id ? null : doc)}
                      className="p-1.5 rounded bg-slate-50 text-slate-600 hover:bg-slate-100" title="Ver detalhes"
                      data-testid={`view-doc-${doc.id}`}>
                      <Eye size={14} />
                    </button>
                    {isSimulated ? (
                      <>
                        <button disabled
                          data-testid={`danfe-disabled-${doc.id}`}
                          title="Documento nao fiscal (simulado)"
                          className="p-1.5 rounded bg-slate-100 text-slate-400 cursor-not-allowed opacity-60">
                          <FileDown size={14} />
                        </button>
                        <button disabled
                          data-testid={`cancel-disabled-${doc.id}`}
                          title="Documento nao fiscal (simulado)"
                          className="p-1.5 rounded bg-slate-100 text-slate-400 cursor-not-allowed opacity-60">
                          <Ban size={14} />
                        </button>
                        <span className="text-[10px] text-rose-600 ml-1 italic" data-testid={`simulada-warn-${doc.id}`}>
                          Documento nao fiscal (simulado)
                        </span>
                      </>
                    ) : (
                      <>
                        {doc.key && (
                          <>
                            <button onClick={() => setPreviewDocId(doc.id)}
                              data-testid={`preview-danfe-${doc.id}`}
                              title="Visualizar DANFE (preview sem valor fiscal)"
                              className="p-1.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100">
                              <Receipt size={14} />
                            </button>
                            <div className="relative inline-block group">
                              <button
                                data-testid={`xml-download-${doc.id}`}
                                title="Baixar XML"
                                className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 inline-flex items-center gap-0.5">
                                <FileDown size={14} />
                                <ChevronDown size={10} />
                              </button>
                              <div className="hidden group-hover:block absolute right-0 top-full z-10 mt-0.5 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden min-w-[220px]">
                                {(doc.status === 'AUTORIZADA' || doc.status === 'EMITIDA' || doc.status === 'CANCELADA') && (
                                  <button onClick={() => handleDownloadXml(doc.id, doc.key, 'proc')}
                                    data-testid={`xml-download-proc-${doc.id}`}
                                    className="w-full px-3 py-2 text-left hover:bg-blue-50 text-xs">
                                    <div className="font-semibold text-slate-800">nfeProc (oficial)</div>
                                    <div className="text-[10px] text-slate-500">Arquivamento fiscal · contador</div>
                                  </button>
                                )}
                                <button onClick={() => handleDownloadXml(doc.id, doc.key, 'signed')}
                                  data-testid={`xml-download-signed-${doc.id}`}
                                  className="w-full px-3 py-2 text-left hover:bg-blue-50 text-xs">
                                  <div className="font-semibold text-slate-800">NFe assinada</div>
                                  <div className="text-[10px] text-slate-500">Validador "Validar XML" SEFAZ</div>
                                </button>
                                <button onClick={() => handleDownloadXml(doc.id, doc.key, 'draft')}
                                  data-testid={`xml-download-draft-${doc.id}`}
                                  className="w-full px-3 py-2 text-left hover:bg-blue-50 text-xs border-t border-slate-100">
                                  <div className="font-semibold text-slate-600">Rascunho</div>
                                  <div className="text-[10px] text-slate-500">Inspeção · sem assinatura</div>
                                </button>
                              </div>
                            </div>
                            {(doc.status === 'PENDENTE' || doc.status === 'REJEITADA') && doc.reference_type === 'ORDER' && doc.reference_id && (
                              <button onClick={() => { setEditDestOrderId(doc.reference_id); setEditDestDocId(doc.id); }}
                                data-testid={`edit-dest-doc-${doc.id}`}
                                title="Editar destinatario (override)"
                                className="p-1.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100">
                                <Settings size={14} />
                              </button>
                            )}
                            {(doc.status === 'PENDENTE' || doc.status === 'REJEITADA') && (
                              <button onClick={async () => {
                                try {
                                  const r = await fiscal.validateDocument(doc.id);
                                  const v = r.data;
                                  if (v.success) {
                                    toast.success('✓ Pré-validação OK — pode transmitir');
                                  } else {
                                    toast.error(
                                      <div>
                                        <div className="font-semibold mb-1">{v.errors.length} erro(s) encontrado(s):</div>
                                        <ul className="text-xs space-y-0.5">
                                          {v.errors.slice(0, 5).map((e, i) => <li key={i}>• {e}</li>)}
                                        </ul>
                                      </div>,
                                      { duration: 10000 }
                                    );
                                  }
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Erro ao validar');
                                }
                              }}
                                data-testid={`validate-doc-${doc.id}`}
                                title="Validar XML localmente (schema + assinatura + totais + QRCode)"
                                className="p-1.5 rounded bg-cyan-50 text-cyan-700 hover:bg-cyan-100">
                                <CheckCircle size={14} />
                              </button>
                            )}
                            {(doc.status === 'PENDENTE' || doc.status === 'REJEITADA') && (
                              <button onClick={() => handleTransmit(doc.id)}
                                disabled={transmittingId === doc.id}
                                data-testid={`transmit-doc-${doc.id}`}
                                title="Assinar e transmitir para SEFAZ"
                                className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-semibold inline-flex items-center gap-1">
                                <Shield size={12} /> {transmittingId === doc.id ? '...' : 'SEFAZ'}
                              </button>
                            )}
                          </>
                        )}
                        {(doc.status === 'EMITIDA' || doc.status === 'AUTORIZADA') && (
                          <button onClick={() => handleCancel(doc.id)} data-testid={`cancel-doc-${doc.id}`}
                            className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100" title="Cancelar">
                            <Ban size={14} />
                          </button>
                        )}
                        {(doc.status === 'ERRO' || doc.status === 'REJEITADA') && (
                          <button onClick={() => handleReprocess(doc.id)} data-testid={`reprocess-doc-${doc.id}`}
                            className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100" title="Reprocessar">
                            <RefreshCw size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {docs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nenhum documento fiscal encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedDoc && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="doc-detail">
          {(selectedDoc.is_simulated || selectedDoc.status === 'SIMULADA') && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-testid="doc-detail-simulada-warn">
              <AlertCircle size={16} className="text-rose-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-700">Documento nao fiscal (simulado)</p>
                <p className="text-xs text-rose-600">Esta nota foi gerada apenas para fins de teste e nao possui validade fiscal junto a SEFAZ.</p>
              </div>
            </div>
          )}
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold text-slate-800">
                <DocTypeBadge type={selectedDoc.doc_type} /> {selectedDoc.number ? `#${selectedDoc.number}` : 'Sem numero'}
                {(selectedDoc.is_simulated || selectedDoc.status === 'SIMULADA') && <SimuladaTag />}
              </h4>
              {selectedDoc.key && <p className="text-xs text-slate-400 font-mono mt-1">Chave: {selectedDoc.key}</p>}
            </div>
            <button onClick={() => setSelectedDoc(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Status:</span> <DocStatusBadge status={selectedDoc.status} /></div>
            <div><span className="text-slate-500">Valor:</span> <span className="font-medium">{formatCurrency(selectedDoc.total_value)}</span></div>
            <div><span className="text-slate-500">Impostos:</span> {formatCurrency(selectedDoc.tax_total)}</div>
            <div><span className="text-slate-500">Ambiente:</span> {selectedDoc.environment}</div>
          </div>
          {selectedDoc.items?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-600 mb-2">Itens:</h5>
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50"><th className="px-3 py-1.5 text-left">Item</th><th className="px-3 py-1.5">CFOP</th><th className="px-3 py-1.5 text-right">Qtd</th><th className="px-3 py-1.5 text-right">Unit</th><th className="px-3 py-1.5 text-right">Total</th></tr></thead>
                <tbody>
                  {selectedDoc.items.map((item, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{item.description}</td>
                      <td className="px-3 py-1.5 text-center font-mono">{item.cfop || '-'}</td>
                      <td className="px-3 py-1.5 text-right">{item.quantity}</td>
                      <td className="px-3 py-1.5 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <DanfePreviewModal docId={previewDocId} onClose={() => setPreviewDocId(null)} onAfterRegenerate={load} />
      {editDestOrderId && (
        <FiscalDestinationModal
          orderId={editDestOrderId}
          mode="edit"
          onClose={() => { setEditDestOrderId(null); setEditDestDocId(null); }}
          onConfirm={async () => {
            // Apos salvar override, regerar o XML para refletir as alteracoes
            const docId = editDestDocId;
            setEditDestOrderId(null);
            setEditDestDocId(null);
            try {
              if (docId) {
                await fiscal.regenerateXml(docId);
                toast.success('XML regenerado com novo destinatario');
                load();
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erro ao regenerar XML');
            }
          }}
        />
      )}
    </div>
  );
}

// ============ EMIT NFC-e TAB ============
function EmitTab() {
  const [orders, setOrders] = useState([]);
  const [emitting, setEmitting] = useState(null);
  const [destOrderId, setDestOrderId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ordersApi.list(),
      fiscal.documents.list({ doc_type: 'NFCE', include_archived: false, page_size: 500 }).catch(() => ({ data: [] })),
    ]).then(([ordersRes, docsRes]) => {
      if (cancelled) return;
      // Pedidos que ja tem NFC-e ativa (qualquer status excepto CANCELADA/REJEITADA)
      const docsRaw = Array.isArray(docsRes?.data) ? docsRes.data : (docsRes?.data?.items || []);
      const blocked = new Set(
        (docsRaw || [])
          .filter(d => d.reference_type === 'ORDER' && d.reference_id)
          .filter(d => !['CANCELADA', 'REJEITADA', 'ARQUIVADA'].includes(String(d.status || '').toUpperCase()))
          .map(d => String(d.reference_id))
      );
      const eligible = (ordersRes.data || []).filter(o =>
        ['ENTREGUE', 'CONFIRMADO', 'PREPARO'].includes(o.status) && !blocked.has(String(o.id))
      );
      setOrders(eligible);
    });
    return () => { cancelled = true; };
  }, []);

  const handleEmit = async (orderId) => {
    setEmitting(orderId);
    try {
      const res = await fiscal.emitNfce(orderId);
      toast.success(`NFC-e #${res.data.number} emitida! ${res.data.simulated ? '(Simulado)' : ''}`);
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao emitir';
      toast.error(detail);
    }
    finally { setEmitting(null); }
  };

  const handleGenerateXml = async (orderId) => {
    setEmitting(orderId);
    try {
      const r = await fiscal.generateXml(orderId);
      toast.success(`XML NFC-e gerado: chave ${r.data.access_key.slice(0, 12)}... (${r.data.xml_size} bytes)`);
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao gerar XML');
    } finally {
      setEmitting(null);
    }
  };

  // Para canais PDV/CARDAPIO_DIGITAL: abre modal "CPF na nota?" antes do gerar
  const supportsDest = (origin) => ['PDV', 'CARDAPIO_DIGITAL'].includes(String(origin || '').toUpperCase());

  const handleClickGenerate = (order) => {
    if (supportsDest(order.origin)) {
      setDestOrderId(order.id);
    } else {
      handleGenerateXml(order.id);
    }
  };

  return (
    <div className="space-y-4" data-testid="fiscal-emit-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="emit-disabled-banner">
        <p className="text-sm text-amber-800 font-semibold">Emissao SEFAZ ainda desabilitada — gerar XML (Etapa 2)</p>
        <p className="text-xs text-amber-700 mt-1">
          A simulacao foi removida. A transmissao real para SEFAZ esta em desenvolvimento.
          Voce pode <strong>gerar o XML estruturado</strong> (com chave de 44 digitos + DV) que ficara como <strong>PENDENTE</strong> ate
          a etapa de assinatura digital + envio.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Pedido</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Origem</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Total</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Acao</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`emit-order-${order.id}`}>
                <td className="px-4 py-3 font-mono text-sm text-slate-700">{String(order.id).slice(0, 8)}</td>
                <td className="px-4 py-3">{order.customer_name || '-'}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-slate-100 text-xs">{order.origin}</span></td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs">{order.status}</span></td>
                <td className="px-4 py-3 text-right font-medium">{formatCurrency(order.total)}</td>
                <td className="px-4 py-3 text-center">
                  {supportsDest(order.origin) && (
                    <button onClick={() => setDestOrderId(order.id)} disabled={emitting === order.id}
                      data-testid={`fiscal-dest-btn-${order.id}`}
                      title="Configurar CPF na nota (opcional)"
                      className="mr-2 bg-slate-100 text-slate-700 px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-200 disabled:opacity-50">
                      CPF na nota
                    </button>
                  )}
                  <button onClick={() => handleClickGenerate(order)} disabled={emitting === order.id}
                    data-testid={`generate-xml-btn-${order.id}`}
                    title="Gerar XML NFC-e (sem transmitir SEFAZ)"
                    className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                    {emitting === order.id ? 'Gerando...' : 'Gerar XML'}
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nenhum pedido elegivel para emissao</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {destOrderId && (
        <FiscalDestinationModal
          orderId={destOrderId}
          mode="capture"
          onClose={() => setDestOrderId(null)}
          onConfirm={async () => {
            const oid = destOrderId;
            setDestOrderId(null);
            await handleGenerateXml(oid);
          }}
        />
      )}
    </div>
  );
}

// ============ TAX PROFILES TAB ============
function TaxProfilesTab() {
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', cfop: '5102', ncm: '21069090', icms_cst: '102', icms_rate: 0, pis_cst: '49', pis_rate: 0, cofins_cst: '49', cofins_rate: 0 });

  const load = useCallback(() => { fiscal.taxProfiles.list().then(r => setProfiles(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await fiscal.taxProfiles.create({ ...form, icms_rate: parseFloat(form.icms_rate), pis_rate: parseFloat(form.pis_rate), cofins_rate: parseFloat(form.cofins_rate) });
      toast.success('Perfil tributario criado');
      setShowForm(false);
      setForm({ name: '', cfop: '5102', ncm: '21069090', icms_cst: '102', icms_rate: 0, pis_cst: '49', pis_rate: 0, cofins_cst: '49', cofins_rate: 0 });
      load();
    } catch { toast.error('Erro'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover perfil?')) return;
    try { await fiscal.taxProfiles.delete(id); toast.success('Removido'); load(); } catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="fiscal-tax-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(!showForm)} data-testid="create-tax-btn"
          className="flex items-center gap-2 bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#e55a5a]">
          <Plus size={16} /> Novo Perfil
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="tax-form">
          <h4 className="font-semibold text-slate-700">Novo Perfil Tributario</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500">Nome</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tax-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500">CFOP</label>
              <input required value={form.cfop} onChange={e => setForm(f => ({ ...f, cfop: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tax-cfop" />
            </div>
            <div>
              <label className="text-xs text-slate-500">NCM</label>
              <input value={form.ncm} onChange={e => setForm(f => ({ ...f, ncm: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tax-ncm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">ICMS CST</label>
              <input value={form.icms_cst} onChange={e => setForm(f => ({ ...f, icms_cst: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tax-icms-cst" />
            </div>
            <div>
              <label className="text-xs text-slate-500">ICMS %</label>
              <input type="number" step="0.01" value={form.icms_rate} onChange={e => setForm(f => ({ ...f, icms_rate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tax-icms-rate" />
            </div>
            <div>
              <label className="text-xs text-slate-500">PIS %</label>
              <input type="number" step="0.01" value={form.pis_rate} onChange={e => setForm(f => ({ ...f, pis_rate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tax-pis-rate" />
            </div>
            <div>
              <label className="text-xs text-slate-500">COFINS %</label>
              <input type="number" step="0.01" value={form.cofins_rate} onChange={e => setForm(f => ({ ...f, cofins_rate: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="tax-cofins-rate" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="tax-submit" className="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-slate-100 px-4 py-2 rounded-lg text-sm">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles.map(p => (
          <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`tax-profile-${p.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-slate-800">{p.name}</h4>
                <p className="text-xs text-slate-500 font-mono mt-1">CFOP: {p.cfop} | NCM: {p.ncm || '-'}</p>
              </div>
              <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100">
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
              <div className="bg-slate-50 rounded p-2 text-center">
                <p className="text-xs text-slate-500">ICMS</p>
                <p className="font-medium text-sm">{p.icms_rate}%</p>
              </div>
              <div className="bg-slate-50 rounded p-2 text-center">
                <p className="text-xs text-slate-500">PIS</p>
                <p className="font-medium text-sm">{p.pis_rate}%</p>
              </div>
              <div className="bg-slate-50 rounded p-2 text-center">
                <p className="text-xs text-slate-500">COFINS</p>
                <p className="font-medium text-sm">{p.cofins_rate}%</p>
              </div>
            </div>
          </div>
        ))}
        {profiles.length === 0 && <p className="text-slate-400 col-span-2 text-center py-8">Nenhum perfil tributario cadastrado</p>}
      </div>
    </div>
  );
}

// ============ CONFIG TAB ============
function CertificateUploadCard() {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    fiscal.certificateInfo().then(r => setInfo(r.data)).catch(() => setInfo({ installed: false }));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Selecione o arquivo .pfx'); return; }
    if (!pwd) { toast.error('Informe a senha do certificado'); return; }
    setBusy(true);
    try {
      const r = await fiscal.uploadCertificate(file, pwd);
      toast.success(`Certificado instalado (CNPJ ${r.data.cnpj})`);
      setFile(null);
      setPwd('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao instalar certificado');
    } finally {
      setBusy(false);
    }
  };

  const expiringSoon = info?.installed && info.valid_until && (new Date(info.valid_until) - Date.now()) < 30 * 86400 * 1000;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="cert-upload-card">
      <div className="flex items-center gap-2">
        <Shield size={18} className="text-emerald-600" />
        <h3 className="font-semibold text-slate-800 text-lg">Certificado Digital A1 (ICP-Brasil)</h3>
      </div>

      {info?.installed ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1" data-testid="cert-installed">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Certificado instalado</span>
          </div>
          <div className="text-xs text-slate-600">CNPJ: <span className="font-mono">{info.cnpj}</span></div>
          <div className={`text-xs ${expiringSoon ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>
            Valido ate: {new Date(info.valid_until).toLocaleDateString('pt-BR')}
            {expiringSoon && ' — VENCENDO EM BREVE!'}
          </div>
          <div className="text-xs text-slate-500">Instalado em: {info.uploaded_at ? new Date(info.uploaded_at).toLocaleString('pt-BR') : '-'}</div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="cert-not-installed">
          Nenhum certificado instalado. Faca upload do arquivo .pfx para habilitar a transmissao SEFAZ.
        </div>
      )}

      <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-sm text-slate-600 font-medium">Arquivo .pfx</label>
          <input
            type="file"
            accept=".pfx,.p12"
            onChange={e => setFile(e.target.files?.[0] || null)}
            data-testid="cert-file-input"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1 bg-white"
          />
        </div>
        <div>
          <label className="text-sm text-slate-600 font-medium">Senha</label>
          <input
            type="password"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            data-testid="cert-password-input"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1"
            autoComplete="off"
          />
        </div>
        <div className="md:col-span-3">
          <button
            type="submit"
            disabled={busy || !file || !pwd}
            data-testid="cert-upload-submit"
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {busy ? 'Validando...' : (info?.installed ? 'Substituir certificado' : 'Instalar certificado')}
          </button>
          <span className="ml-3 text-xs text-slate-500">
            A senha sera criptografada (Fernet) e o CNPJ do cert sera validado contra a loja.
          </span>
        </div>
      </form>
    </div>
  );
}

function ConfigTab() {
  return (
    <div className="space-y-6" data-testid="fiscal-config-tab">
      <CertificateUploadCard />
    </div>
  );
}

// ============ REPORTS TAB ============
function ReportsTab() {
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fiscal.reports.summary(days).then(r => setReport(r.data));
  }, [days]);

  if (!report) return <div className="text-center py-8 text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-4" data-testid="fiscal-reports-tab">
      <div className="flex gap-2">
        {[7, 15, 30, 60, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`report-period-${d}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${days === d ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
            {d} dias
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total Documentos</p>
          <p className="text-2xl font-bold text-slate-800">{report.total_documents}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
          <p className="text-xs text-emerald-600">NFC-e Emitidas</p>
          <p className="text-2xl font-bold text-emerald-700">{report.total_nfce}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <p className="text-xs text-blue-600">NF-e Emitidas</p>
          <p className="text-2xl font-bold text-blue-700">{report.total_nfe}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-4">
          <p className="text-xs text-red-600">Canceladas</p>
          <p className="text-2xl font-bold text-red-700">{report.total_canceladas}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-500 mb-1">Faturamento Fiscal</h4>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(report.faturamento_total)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-500 mb-1">Impostos Totais</h4>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(report.impostos_total)}</p>
        </div>
      </div>

      {report.by_day.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="font-medium text-slate-700 mb-3">Faturamento por Dia</h4>
          <div className="space-y-2">
            {report.by_day.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-slate-50">
                <span className="text-slate-600">{d.date}</span>
                <div className="flex gap-4">
                  <span className="text-slate-500">{d.count} docs</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(d.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MAIN PAGE ============
export function FiscalPage() {
  const [activeTab, setActiveTab] = useState('documents');

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="fiscal-page">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Fiscal
          </h1>
          <p className="text-slate-600">NFC-e, NF-e, perfis tributarios e configuracoes fiscais</p>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto" data-testid="fiscal-tabs">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`fiscal-tab-${tab.id}`}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'documents' && <DocumentsTab />}
        {activeTab === 'emit' && <EmitTab />}
        {activeTab === 'tax' && <TaxProfilesTab />}
        {activeTab === 'config' && <ConfigTab />}
        {activeTab === 'reports' && <ReportsTab />}
      </div>
    </MainLayout>
  );
}
